import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, saveUser, BACKEND_URL } from "../api";
import { DiagnosticArchive } from "../components/DiagnosticArchive";
import { ExternalLink } from "lucide-react";

// Vista Esami diagnostici incorporata nel Manager Windows PAM Connect (WebView2).
// Riusa i componenti reali della pagina originale; nessun header/menu/footer YouPAM.
export default function PamcEmbed() {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState(false);
  const readyRef = useRef(false);

  // Applica l'embed_token ricevuto (canale WebView2 o fallback) e idrata il profilo.
  const applyToken = useCallback(async (tok) => {
    if (!tok) return;
    localStorage.setItem("token", tok);
    if (readyRef.current) { setErr(false); return; } // già attiva: aggiorna solo lo storage (rinnovo invisibile)
    try { const { data } = await api.get("/auth/me"); saveUser(data); readyRef.current = true; setReady(true); setErr(false); }
    catch { setErr(true); }
  }, []);

  // Bootstrap sessione: PREFERITO il canale messaggi WebView2 (token mai nell'URL); fallback al fragment #t=.
  useEffect(() => {
    const wv = window.chrome?.webview;
    const onMsg = (e) => {
      const d = e && e.data;
      if (typeof d === "string" && d.startsWith("pamc-session:")) applyToken(d.slice(13));
    };
    if (wv) { try { wv.addEventListener("message", onMsg); wv.postMessage("pamc-request-session"); } catch { /* host non disponibile */ } }
    // Fallback (ambienti/test senza host WebView2): token nel fragment, rimosso subito dall'URL.
    const m = window.location.hash.match(/[#&]t=([^&]+)/);
    if (m) { window.history.replaceState({}, "", "/pamc-embed"); applyToken(decodeURIComponent(m[1])); }
    // Riapertura del Manager: usa il token già presente nello storage isolato della WebView.
    const to = setTimeout(() => {
      if (!readyRef.current) { const s = localStorage.getItem("token"); if (s) applyToken(s); else if (!wv) setErr(true); }
    }, wv ? 4000 : 400);
    return () => { if (wv) { try { wv.removeEventListener("message", onMsg); } catch { /* noop */ } } clearTimeout(to); };
  }, [applyToken]);

  // Auto-rinnovo invisibile (TTL server 30 min, rinnovo a 25). La consultazione non scade finché l'associazione è valida.
  useEffect(() => {
    const id = setInterval(async () => {
      try { const { data } = await api.post("/pam-connect/embed/renew"); if (data.embed_token) localStorage.setItem("token", data.embed_token); }
      catch { try { window.chrome?.webview?.postMessage("pamc-embed-reauth"); } catch { /* host non disponibile */ } setErr(true); }
    }, 25 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Apre SEMPLICEMENTE la pagina principale Esami diagnostici nel browser esterno. Nessun token, nessun parametro studio/filtri.
  const openInYouPam = () => {
    const url = `${window.location.origin || BACKEND_URL}/drive?tab=exams`;
    try { window.chrome?.webview?.postMessage("pamc-open-youpam:" + url); } catch { /* host non disponibile */ }
    window.open(url, "_blank", "noopener");
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="pamc-embed-error">
      <p className="text-slate-700 text-sm text-center max-w-sm">{t("diag.embed_error")}</p>
    </div>);

  if (!ready) return <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="pamc-embed-loading"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pamc-embed">
      <div className="relative w-full max-w-[1600px] mx-auto px-3 sm:px-5 py-4 overflow-x-auto">
        <button onClick={openInYouPam} title={t("diag.embed_open_youpam")} data-testid="pamc-embed-open-youpam"
          className="absolute top-4 right-3 sm:right-5 z-20 yp-btn yp-btn-outline !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1.5">
          <ExternalLink size={14} /><span className="hidden sm:inline">{t("diag.embed_open_youpam")}</span>
        </button>
        <div className="min-w-[640px]">
          <DiagnosticArchive />
        </div>
      </div>
    </div>);
}
